import React, { useState, useEffect, useRef } from "react";
import { navigate } from "gatsby"
import Flink from '~/components/shared/flink'
import { useSearchContentQuery } from "~/hooks/useSearchContentQuery"
import useKeyboardEvent from '~/utils/keyboard-evt'
import useSearchHook from '~/utils/use-search'
import { useTheme } from '~/contexts/ThemeContext'
import useAnalytics from '~/utils/analytics'
import { imgSize, isStag, jnstyls, compact } from '~/utils/helpers'

import IconClose from '~/components/icons/close'
import IconSearch from '~/components/icons/search'

import { aria_btn, solid_btn } from "~/styles/app.module.scss"
import * as styles from "~/styles/testing/test_search_panel.module.scss"


const TestSearch = () => {
  const { isMobile, setFreeze, setSearch, analytics, setAnalytics, setBread, gender, setGender } = useTheme()
  const content = useSearchContentQuery()
  const { ga_search_click, elev_search, elev_select } = useAnalytics()

  let initgend = gender?.srGender?.slice(0,1) || null

  const [mobiHeight, setMobiHeight] = useState(false)
  const [searchData, setSearchData] = useState(null)
  const [prodResults, setProdResults] = useState([])
  const [searchType, setSearchType] = useState(initgend)
  const [currSort, setCurrSort] = useState('best')
  const [currSizes, setCurrSizes] = useState([])
  const [sizes, setSizes] = useState(null)
  const [dontGuess, setDontGuess] = useState(false)
  const [suggests, setSuggests] = useState([])
  const [searchTest, setSearchTest] = useState(false)

  const searchApi = useSearchHook({searchType, currSort, currSizes, setSuggests, dontGuess, plstest: true})
  const { getSearchData, searchProds, filterSizes, getSearch, saveSearch } = searchApi

  const { closeListen } = useKeyboardEvent()

  const panelRef = useRef(null)
  const inptRef = useRef(null)
  const resultsRef = useRef(null)
  const searchTimeout = useRef(null)

  const handleClose = () => {
    const panel_el = panelRef.current

    if (!panel_el) return
    panel_el.setAttribute('aria-hidden', 'true')
    setTimeout(() => {
      setFreeze(false)
      setSearch(false)
    }, 200)
  }

  const searchClear = () => {
    setProdResults([])
    setCurrSort('best')
    setSizes(null)
    setCurrSizes([])
    setSuggests([])
    setDontGuess(false)
    localStorage.removeItem('search')
    inptRef.current.value = ''
  }

  const debounce = (func, delay) => {
    return (...args) => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current)
      }
      searchTimeout.current = setTimeout(() => {
        func(...args)
      }, delay)
    }
  }

  const handleSearch = (e) => {
    if (!searchData) return

    const val = inptRef.current.value.toLowerCase()

    if ( val === '' ) {
      searchClear()
      return
    }
    if (!!e && dontGuess) {
      setDontGuess(false)
      return
    }

    if ( val.length < 3 ) return

    let gendvals = compact(val.split(' '))
    const gends = ['men', 'mens', "men's", 'women', 'womens', "women's"]
    let has_gend = gendvals.find(x => gends.includes(x.toLowerCase()))
    let search_gend = has_gend?.toLowerCase()?.slice(0,1)
    if (has_gend && search_gend !== searchType) {
      setSearchType(search_gend)
      return
    }

    const prods = searchProds({val})
    if (!prods) return

    setProdResults(prods)
    if (localStorage.getItem('csearch')) parseSizes(prods)

    do_saving()
    const anaprods = prods.flatMap(x => x.feats)
    debounce(elev_search, 2000)(val, anaprods.slice(0,36))
  }

  const toggleSize = (size) => {
    if (currSizes.includes(size)) {
      setCurrSizes(currSizes.filter(x => x !== size))
      return
    }
    setCurrSizes([...new Set([...currSizes, size])])
  }

  const parseSizes = (product_lines) => {
    const prods = product_lines.flatMap(x => x.feats)

    let all_sizes = [...new Set( prods.flatMap(product => Object.keys(product.variants)) )]
    all_sizes = orderSizes(all_sizes).filter(x => x.length > 0)

    let avail_sizes = prods.flatMap(product => {
      return compact(Object.keys(product.variants).map(k => !!product.variants[k] ? k : null))
    })
    avail_sizes = [...new Set(compact(avail_sizes))]
    setSizes({all: all_sizes, avail: avail_sizes})
  }

  const orderSizes = (sizes) => {
    const size_order = ['xxs','2s','xs','s','m','l','xl','xxl','2x','xxxl','3x','os','one size']
    let str_sizes = sizes.filter(x => !Number.isInteger(Number(x)) && !x.includes('.'))
    str_sizes = str_sizes.sort((a, b) => size_order.indexOf(a.toLowerCase()) - size_order.indexOf(b.toLowerCase()) )

    let int_sizes = sizes.filter(x => Number.isInteger(Number(x)) || x.includes('.'))
    int_sizes = int_sizes.sort((a, b) => parseFloat(a) - parseFloat(b))

    let shoe_sizes = int_sizes.filter(x => x < 20)
    int_sizes = int_sizes.filter(x => !shoe_sizes.includes(x))
    return [str_sizes, int_sizes, shoe_sizes]
  }

  const gaClick = (product=false, isPopular=false, index=false) => {
    const val = inptRef.current.value

    const term = isPopular ? isPopular.toLowerCase() : inptRef.current.value
    const type = searchType === null ? 'all' : searchType === 'm' ? 'men' : searchType === 'w' ? 'women' : searchType

    ga_search_click(term, type, !!isPopular)
    if (product) {
      setAnalytics({...analytics, list: 'search results'})
      elev_select(product, index, 'search results')
    }
  }

  const get_search_data = async () => {
    const data = await getSearchData({})
    setSearchData(data)
  }

  const do_saving = async () => {
    const val = inptRef.current.value
    if ( val === '' ) return

    const prods = prodResults.flatMap(x => x.feats)

    const obj = {
      prodResults: prods,
      searchType: searchType,
      currSort
    }

    const vals_arr = compact(val.split(' '))
    const has_saved = await saveSearch(obj, vals_arr)
    return has_saved
  }

  const goto_page = async () => {
    const has_saved = await do_saving()
    if (has_saved) {
      if (window.location.pathname.includes('/search')) {
        window.location = `/search`
        handleClose()
      } else {
        navigate('/search')
        handleClose()
      }
    }
  }

  const check_enter = (e) => {
    if (e.key !== 'Enter' || !searchData) return
    goto_page()
  }

  const checkSize = (e) => {
    if (!isMobile) {
      if (!!mobiHeight) setMobiHeight(null)
      return
    }
    const max = `${window.visualViewport.height}px`
    setMobiHeight(max)
  }

  const countText = () => {
    const num = prodResults.flatMap(x => x.feats).length;
    return `${num} result${num !== 1 ? 's' : ''}`
  }

  const calloutText = (id) => {
    return searchData.callouts.find(x => x.id === id)?.data || {}
  }

  const tagMatch = (matches, tag) => {
    return matches.includes(tag) || matches.find(x => tag.includes(x))
  }

  const pubString = (stamp) => {
    const pub = new Date(stamp * 1000)
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

    return `${months[pub.getMonth()]} ${pub.getDate()} ${pub.getFullYear()}`
  }


  useEffect(() => {
    setBread({handle: 'test-search', title: 'Search Results'})
    if (localStorage.getItem('csearch')) setSearchTest(true)

    closeListen(handleClose)

    if (!searchData) get_search_data()
    if (!prodResults) localStorage.removeItem('search')
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!inptRef.current) return
    setTimeout(() => {
      if (inptRef.current) inptRef.current.focus()
    }, 100)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inptRef])

  useEffect(() => {
    if (!searchData) return
    resultsRef.current.scrollTo({
      top: 0,
      behavior: 'instant'
    })
    handleSearch()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchType, currSort, dontGuess])

  useEffect(() => {
    if (!searchData) return
    if (!sizes) return
    if (currSizes.length < 1 && !!sizes) {
      handleSearch()
      return
    }
    setProdResults( filterSizes({results: prodResults, val: inptRef.current.value}) )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currSizes])
  
  useEffect(() => {
    setFreeze(true)
    const panel = panelRef.current
    setTimeout(() => {
      panel.setAttribute('aria-hidden', 'false')
    }, 100)
  }, [setFreeze])

  useEffect(() => {
    window.addEventListener('keydown', check_enter)
    window.visualViewport.addEventListener('resize', checkSize)

    return () => {
      window.removeEventListener('keydown', check_enter)
      window.visualViewport.removeEventListener('resize', checkSize)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  })


  return (
    <section 
      className={styles.searchwr} 
      aria-label="Search products" 
      role="dialog" 
      aria-modal="true" 
      aria-hidden="true"
      ref={panelRef}
      style={{maxHeight: mobiHeight ? mobiHeight : ''}}
    >
      <button 
        type="button"
        className={[aria_btn, styles.close].join(' ')}
        aria-label="Close sidebar" 
        onClick={(e)=> {handleClose()}}
      >
        <IconClose icon_classes={''} />
      </button>
      <div className={styles.head}>
        <div className={styles.inputwr}>
          <input 
            type="search"
            name="q"
            placeholder="Search our store"
            className={styles.search_inpt}
            onChange={(e)=>{handleSearch(e)}}
            ref={inptRef}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck="false"
          />
          <div className={styles.search_btn}>
            <IconSearch icon_classes={''} />
          </div>
          <div className={styles.search_gender} >
            <button 
              type="button"
              className={`${aria_btn} ${styles.gender_btn}`} 
              aria-label="Search all products" 
              aria-current={searchType === null ? 'true' : 'false'}
              onClick={(e)=>{setSearchType(null)}}
            >
              All
            </button>
            <button 
              type="button"
              className={`${aria_btn} ${styles.gender_btn}`} 
              aria-label="Search mens products" 
              aria-current={searchType === 'm' ? 'true' : 'false'}
              onClick={(e)=>{setSearchType('m')}}
            >
              Men's
            </button>
            <button 
              type="button"
              className={`${aria_btn} ${styles.gender_btn}`} 
              aria-label="Search womens products" 
              aria-current={searchType === 'w' ? 'true' : 'false'}
              onClick={(e)=>{setSearchType('w')}}
            >
              Women's
            </button>
            <div className={styles.search_extra}>
              {inptRef?.current?.value != '' && (
                <button
                  type='button'
                  className={styles.clear}
                  onClick={(e) => searchClear()}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {searchTest && suggests.length > 0 && (
        <div className={styles.suggestswr}>
          
          {(suggests && !dontGuess) ? (
            <>
              <div className={styles.suggests}>
                <div className={styles.suggestlab}>
                  Showing results for:
                </div>
                {suggests.map((term,index) =>
                  <div key={`${term}_${index}`} className={styles.suggest}>{term}</div>
                )}
              </div>
              <div className={styles.suggests}>
                <div className={styles.suggestlab}>
                  Search instead for:
                </div>
                <button
                  type='button'
                  className={styles.suggestbtn}
                  onClick={(e) => setDontGuess(true)}
                >
                  {inptRef.current.value}
                </button>
              </div>
            </>
          ) : (
            <div className={styles.suggests}>
              <div className={styles.suggestlab}>
                Search instead for:
              </div>
              <button
                type='button'
                className={styles.suggestbtn}
                onClick={(e) => setDontGuess(false)}
              >
                {suggests.join(' ')}
              </button>
            </div>
          )}
        </div>
      )}
      <div className={styles.resultswr} style={{maxHeight: (prodResults.length > 0) ? '90dvh' : 0}}>
        <div 
          className={styles.results} 
          ref={resultsRef}
        >
          {(prodResults.length > 0) && (
            <div className={styles.restitle}>
              <div className={styles.title}>{countText()}</div>
              <div className={styles.count_sorts}>
                <div className={styles.sorts}>
                  <button
                    type='button'
                    className={styles.sort}
                    aria-current={(currSort === 'best').toString()}
                    onClick={(e) => setCurrSort('best')}
                  >
                    Best Sellers
                  </button>
                  <button
                    type='button'
                    className={styles.sort}
                    aria-current={(currSort === 'rec').toString()}
                    onClick={(e) => setCurrSort('rec')}
                  >
                    Recent
                  </button>
                  <button
                    type='button'
                    className={styles.sort}
                    aria-current={(currSort === 'asc').toString()}
                    onClick={(e) => setCurrSort('asc')}
                  >
                    Price
                  </button>
                </div>
              </div>
              {(!!sizes && (prodResults.length > 1 || !!sizes)) && (
                <div className={styles.navsizeswr}>
                  {sizes.all.map((group, sindx) =>
                    <div key={`sizegrp_${sindx}`} className={styles.navsizes}>
                      {group.map(size =>
                        <button
                          key={`navsize_${size}`} 
                          type='button'
                          className={styles.navsize}
                          disabled={sizes.avail.includes(size) ? undefined : 'disabled'}
                          onClick={()=>toggleSize(size)}
                          aria-selected={(currSizes.includes(size)).toString()}
                        >
                          {size}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {prodResults.length > 0 && prodResults.map((product_line,index) => 
            <div key={`searchpl_${product_line.pl_id}`} className={jnstyls([styles.searchpl, styles.testpl])}>
              <div className={styles.pltitle}>
                <div className={styles.testtitle}>
                  {(!searchType && searchType !== 'u') && (
                    <>
                      {product_line.gender === 'm' ? "Men's " : product_line.gender === 'w' ? "Women's " : ''}
                    </>
                  )}
                  {product_line.pl_name}
                </div>
                <div className={styles.plprice}>
                  ${product_line.price}
                </div>
              </div>
              {product_line.feats.map((result, pindex) => {
                let image = result.media.length > 0 ? imgSize(result.media[0],300) : false
                return (
                  <Flink 
                    key={result.handle} 
                    href={`/products/${result.handle}`} 
                    className={jnstyls([styles.item, styles.testitem])} 
                    result=""
                    onClick={(e)=>{
                      handleClose();
                      gaClick(result, false, index)
                    }}
                  >
                    {image && (
                      <div className={styles.image}>
                        <img src={image} alt={result.title} className={styles.img} loading="lazy" width="400" height="532" />
                      </div>
                    )}
                    <div className={styles.info}>
                      <div className={styles.item_title}>
                        <div className={styles.item_name} style={{textTransform: 'capitalize'}}>
                          {result?.color?.name}
                        </div>
                        <div className={styles.variants}>
                          {localStorage.getItem('csearch') && result.variants && Object.keys(result.variants).map(size => 
                            <div
                              key={`${result.mid}_var_${size}`} 
                              className={styles.variant}
                              disabled={!!result.variants[size] ? undefined : 'disabled'}
                            >
                              {size}
                            </div>
                          )}
                        </div>
                      </div>

                      {(result.callouts?.length > 0 || result.altcuts) && (
                        <div className={jnstyls([styles.info, styles.infoextra])}>
                          {result.callouts?.length > 0 && (
                            <div className={styles.callouts} style={{textTransform: 'capitalize'}}>
                              {result.callouts.map(id =>
                                <div 
                                  key={`prod_${result.mid}_call_${id}`} 
                                  className={styles.callout} 
                                  style={{color: calloutText(id)?.color || ''}}
                                >
                                  {calloutText(id).name}
                                </div>
                              )}
                            </div>
                          )}
                          {result.altcuts && (
                            <div className={styles.alts} style={{textTransform: 'capitalize'}}>
                              <div className={styles.altlabel}>
                                {result.altcuts.os.length} {result.altcuts.t}{result.altcuts.os.length !== 1 ? 's' : ''}
                              </div>
                              {result.altcuts.os.map(item =>
                                <div key={`prod_${result.mid}_alt_${item}`}className={styles.altitem}>
                                  {item}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    
                      {false && (
                        <>
                          <div className={styles.data}>
                            <div className={styles.ditem}>
                              <span>Bests Index</span>
                              {result.best}
                            </div>
                            <div className={styles.ditem}>
                              <span>Broken?</span>
                              {result.broken?.toString() || '?'}
                            </div>
                            <div className={styles.ditem}>
                              <span>Search Score</span>
                              {result.score}%
                            </div>
                            <div className={styles.ditem}>
                              <span>Exacts</span>
                              {result.hits}
                            </div>
                            <div className={styles.ditem}>
                              <span>Published</span>
                              {pubString(result.pub)}
                            </div>
                          </div>
                          <div className={styles.data}>
                            <div className={styles.ditem} style={{flex: '0 1 auto'}}>
                              <span>Fuse Hits</span>
                              <div className={styles.dtags}>
                                {result.matches.map((x, index) => 
                                  <div key={`ftag_${result.mid}_${x}_${index}`} className={jnstyls([styles.dtag])}>
                                    {x}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className={styles.ditem}>
                              <span>Tags</span>
                              <div className={styles.dtags}>
                                {result.search.map((x,index) => 
                                  <div key={`ptag_${result.mid}_${x}_${index}`} className={jnstyls([styles.dtag, tagMatch(result.matches, x) ? styles.match : ''])}>
                                    {x}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </Flink>
                )
              })}
              {product_line.products.length > 0 && (
                <PlOtherColors 
                  products={product_line.products}
                  index={index}
                  handleClose={handleClose}
                  gaClick={gaClick}
                />
              )}
            </div>
          )}
        </div>
        <div className={styles.foot}>
          <button type="button" className={`${solid_btn} ${styles.btn}`} onClick={(e)=>{goto_page()}}> 
            See All Results
          </button>
        </div>
      </div>
      <div className={styles.blind} style={{maxHeight: (prodResults.length > 0) ? '0' : ''}}>
        <div className={styles.popular}>
          {(!searchType || searchType === 'm') && content?.mens_links?.length > 0 && (
            <div className={styles.pop_group}>
              <div className={styles.title}>
                Popular Men's Searches
              </div>
              <ol className={styles.popular_list}>
                {content.mens_links.map(link => 
                  <li key={link.link_text} className={styles.popular_item}>
                    <Flink 
                      href={link.url} 
                      aria-label={`Popular Search: ${link.link_text}`}
                      onClick={(e)=>{
                        handleClose();
                        gaClick(false,link.link_text)
                      }} 
                    >
                      {link.link_text}
                    </Flink>
                  </li>
                )}
              </ol>
            </div>
          )}
          {(!searchType || searchType === 'w') && content?.womens_links?.length > 0 && (
            <div className={styles.pop_group}>
              <div className={styles.title}>
                Popular Women's Searches
              </div>
              <ol className={styles.popular_list}>
                {content.womens_links.map(link => 
                  <li key={link.link_text} className={styles.popular_item}>
                    <Flink 
                      href={link.url} 
                      aria-label={`Popular Search: ${link.link_text}`}
                      onClick={(e)=>{
                        handleClose();
                        gaClick(false,link.link_text)
                      }} 
                    >
                      {link.link_text}
                    </Flink>
                  </li>
                )}
              </ol>
            </div>
          )}
        </div>
      </div>

    </section>
  )
}


const PlOtherColors = ({ products, index, handleClose, gaClick}) => {
  const [currColor, setCurrColor] = useState(null)

  return (
    <div className={styles.plcolorswr}>
      <div className={styles.testtitle}>
        {currColor ? 
          currColor
        : `And ${products.length} other Color${products.length > 1 ? 's' : ''}`
        }
      </div>
      <div className={styles.plcolors}>
        {products.slice(0,8).map((result, pindex) => {
          let image = result.media.length > 0 ? imgSize(result.media[0],300) : false

          return (
            <Flink 
              key={result.handle} 
              href={`/products/${result.handle}`} 
              className={jnstyls([styles.plcolor])} 
              result=""
              onClick={(e)=>{
                handleClose();
                gaClick(result, false, index)
              }}
              onMouseEnter={(e)=>setCurrColor(result?.color?.name)} 
              onMouseLeave={(e)=>setCurrColor(null)} 
            >
              {image && (
                <div className={styles.image}>
                  <img src={image} alt={result.title} className={styles.img} loading="lazy" width="400" height="532" />
                </div>
              )}
            </Flink>
          )
        })}
      </div>
    </div>
  )
}




export default TestSearch